import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchManagerHome } from "../services/api";
import { useT } from "../i18n/useT";
import { NavIcon } from "../components/NavIcons";
import { ROUTINES, DEPT, isTicked, setTicked, stepDone, visibleSteps } from "../services/routines";

// A guided daily routine — Morning open or End of day. Each step spans a
// department; the manager walks the list top to bottom, ticking manual
// checks and watching auto steps (open tabs, pending usage) clear
// themselves as the underlying work gets done. State resets each day.
export default function RoutinePage() {
  const { which } = useParams();
  const routine = ROUTINES[which];
  const { t } = useT();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, force] = useState(0); // re-render after a manual tick

  useEffect(() => {
    setLoading(true);
    fetchManagerHome().then(setD).catch(() => {}).finally(() => setLoading(false));
  }, [which]);

  if (!routine) {
    return <p className="muted">Unknown routine.</p>;
  }

  const toggle = (step) => {
    if (step.auto) return; // auto steps mirror live data, not manual ticks
    setTicked(which, step.key, !isTicked(which, step.key));
    force((n) => n + 1);
  };

  const steps = visibleSteps(routine);
  const doneCount = steps.filter((s) => stepDone(which, s, d)).length;
  const total = steps.length;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = !loading && doneCount === total;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <button className="btn secondary small" onClick={() => nav("/home")} style={{ marginBottom: 14 }}>
        ← {t("rt_back")}
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ width: 52, height: 52, borderRadius: 14, background: "#EAF3DE", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
          <NavIcon name={routine.icon} size={30} />
        </span>
        <div>
          <h1 style={{ margin: 0 }}>{t(routine.title)}</h1>
          <div className="muted" style={{ fontSize: 14 }}>{t(routine.sub)}</div>
        </div>
      </div>

      <div style={{ marginTop: 18, marginBottom: 6, display: "flex", justifyContent: "space-between", fontSize: 13.5 }}>
        <span style={{ fontWeight: 600 }}>{t("rt_progress").replace("{n}", doneCount).replace("{m}", total)}</span>
        <span className="muted">{pct}%</span>
      </div>
      <div style={{ height: 8, borderRadius: 6, background: "#eef2f2", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: "#3B9E6E", transition: "width 220ms ease-out" }} />
      </div>

      {allDone && (
        <div style={{ marginTop: 16, padding: "14px 16px", background: "#f2f9f8", border: "1px solid #d7ece3", borderRadius: 12 }}>
          <b style={{ color: "#1D9E75" }}>✓ {t("rt_all_done")}</b>
          <div className="muted" style={{ fontSize: 13 }}>{t("rt_all_done_sub")}</div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
        {steps.map((step) => {
          const dep = DEPT[step.dept];
          const done = stepDone(which, step, d);
          const cnt = step.count ? step.count(d || {}) : 0;
          const showLeft = step.auto && cnt > 0; // outstanding backlog (red)
          return (
            <div
              key={step.key}
              onClick={() => toggle(step)}
              style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 16px", background: "#fff", border: "1px solid " + (done ? "#cfe8db" : "#eef2f2"), borderRadius: 14, cursor: step.auto ? "default" : "pointer" }}
            >
              <span style={{ width: 26, height: 26, borderRadius: "50%", flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", border: done ? "none" : "2px solid #cbd5d3", background: done ? "#3B9E6E" : "transparent", color: "#fff", fontSize: 15 }}>
                {done ? "✓" : ""}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, textDecoration: done ? "line-through" : "none", color: done ? "#5f6b78" : "#1b2a2f" }}>
                  {t(step.key)}
                </div>
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{t(step.key + "_d")}</div>
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: dep.tint, color: dep.ink }}>{t(dep.k)}</span>
                  {cnt > 0 && (
                    <span style={{ fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: showLeft ? "#FCEBEB" : "#FAEEDA", color: showLeft ? "#A32D2D" : "#8A5A0B" }}>
                      {showLeft ? t("rt_left").replace("{n}", cnt) : cnt}
                    </span>
                  )}
                </div>
              </div>
              <button className="btn secondary small" onClick={(e) => { e.stopPropagation(); nav(step.to); }}>
                {t("rt_open")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
